/**
 * `notificationsApi` — standalone RTK Query slice for the proxy-server
 * Notifications surface (bell popover, `/notifications` page, operator
 * alarms).
 *
 * Split off from `ontologyApi` so notification reads/writes own their
 * own cache + tag space (`'Notification'`) instead of sharing the
 * catch-all `ontologyApi` tag soup. Shares `ontologyBaseQuery` and
 * `PROXY_API_V1_URL` with the other proxy-backed slices via
 * `ontology-api-base.ts`.
 *
 * This slice owns notification cache invalidation independently from
 * custom-agent run/config caches.
 */

import { createApi } from '@reduxjs/toolkit/query/react'

import { PROXY_API_V1_URL, ontologyBaseQuery } from '../core'
import type {
  Notification,
  NotificationCounts,
  NotificationKindAudienceList,
  NotificationListFilters,
  NotificationPreferences,
  UpdateNotificationPayload
} from './types'

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

export const NOTIFICATIONS_TAG_TYPES = [
  'Notification',
  'NotificationCounts',
  'NotificationPreferences',
  'NotificationKindAudience'
] as const
export type NotificationsTagType = (typeof NOTIFICATIONS_TAG_TYPES)[number]

export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  baseQuery: ontologyBaseQuery,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: NOTIFICATIONS_TAG_TYPES,
  endpoints: (builder) => ({
    closeNotificationOccurrence: builder.mutation<
      unknown,
      {
        occurrenceId: string
        operationKey: string
        expectedVersion: number
        expectedTaskSetVersion: number
      }
    >({
      query: ({ occurrenceId, operationKey, expectedVersion, expectedTaskSetVersion }) => ({
        url: `${PROXY_API_V1_URL}/problem-occurrences/${encodeURIComponent(occurrenceId)}:close`,
        method: 'POST',
        body: {
          operation_key: operationKey,
          expected_version: expectedVersion,
          expected_task_set_version: expectedTaskSetVersion,
          reason: 'Marked resolved from notification'
        }
      }),
      invalidatesTags: [{ type: 'Notification', id: 'LIST' }, 'NotificationCounts']
    }),
    listNotifications: builder.query<Notification[], NotificationListFilters | void>({
      query: (filters) => ({
        url: `${PROXY_API_V1_URL}/notifications`,
        params: (filters ?? undefined) as Record<string, string | number | boolean | undefined>
      }),
      transformResponse: (
        response: {
          notifications: Notification[]
          next_page_token?: string
          total_size?: number
        } | null
      ) => asArray<Notification>(response?.notifications),
      providesTags: (result) =>
        result
          ? [
              ...result.map((a) => ({ type: 'Notification' as const, id: a.id })),
              { type: 'Notification' as const, id: 'LIST' }
            ]
          : [{ type: 'Notification' as const, id: 'LIST' }]
    }),
    getNotification: builder.query<Notification, string>({
      query: (id) => `${PROXY_API_V1_URL}/notifications/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Notification', id }]
    }),
    updateNotification: builder.mutation<
      Notification,
      { id: string; patch: UpdateNotificationPayload }
    >({
      query: ({ id, patch }) => ({
        url: `${PROXY_API_V1_URL}/notifications/${id}`,
        method: 'PATCH',
        body: patch
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Notification', id },
        { type: 'Notification', id: 'LIST' },
        'NotificationCounts'
      ]
    }),
    getNotificationCounts: builder.query<NotificationCounts, void>({
      query: () => `${PROXY_API_V1_URL}/notifications:counts`,
      providesTags: ['NotificationCounts']
    }),
    /** Marks rows read for the caller; `all` covers every unread row in `bucket`. */
    markNotificationsRead: builder.mutation<
      { marked: number },
      { ids?: string[]; all?: boolean; bucket?: 'action' | 'activity' }
    >({
      query: (body) => ({
        url: `${PROXY_API_V1_URL}/notifications:markRead`,
        method: 'POST',
        body
      }),
      invalidatesTags: [{ type: 'Notification', id: 'LIST' }, 'NotificationCounts']
    }),
    /** A person decided the row needs no action. */
    dismissNotification: builder.mutation<Notification, string>({
      query: (id) => ({
        url: `${PROXY_API_V1_URL}/notifications/${encodeURIComponent(id)}:dismiss`,
        method: 'POST'
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'Notification', id },
        { type: 'Notification', id: 'LIST' },
        'NotificationCounts'
      ]
    }),
    getNotificationPreferences: builder.query<NotificationPreferences, void>({
      query: () => `${PROXY_API_V1_URL}/notifications/preferences`,
      providesTags: ['NotificationPreferences']
    }),
    updateNotificationPreferences: builder.mutation<
      NotificationPreferences,
      NotificationPreferences
    >({
      query: (body) => ({
        url: `${PROXY_API_V1_URL}/notifications/preferences`,
        method: 'PUT',
        body
      }),
      invalidatesTags: [
        'NotificationPreferences',
        'NotificationCounts',
        { type: 'Notification', id: 'LIST' }
      ]
    }),
    listNotificationKindAudiences: builder.query<NotificationKindAudienceList, void>({
      query: () => `${PROXY_API_V1_URL}/notifications/kind-audiences`,
      providesTags: ['NotificationKindAudience']
    }),
    updateNotificationKindAudience: builder.mutation<
      NotificationKindAudienceList,
      { kind: string; roles: string[] }
    >({
      query: ({ kind, roles }) => ({
        url: `${PROXY_API_V1_URL}/notifications/kind-audiences/${encodeURIComponent(kind)}`,
        method: 'PUT',
        body: { roles }
      }),
      invalidatesTags: ['NotificationKindAudience']
    }),
    resetNotificationKindAudience: builder.mutation<NotificationKindAudienceList, string>({
      query: (kind) => ({
        url: `${PROXY_API_V1_URL}/notifications/kind-audiences/${encodeURIComponent(kind)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['NotificationKindAudience']
    })
  })
})

export const {
  useCloseNotificationOccurrenceMutation,
  useListNotificationsQuery,
  useGetNotificationQuery,
  useUpdateNotificationMutation,
  useGetNotificationCountsQuery,
  useMarkNotificationsReadMutation,
  useDismissNotificationMutation,
  useGetNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
  useListNotificationKindAudiencesQuery,
  useUpdateNotificationKindAudienceMutation,
  useResetNotificationKindAudienceMutation
} = notificationsApi
